import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { IosShare as IosShareIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';

interface PricePromoPriceButtonProps {
  /** Formatted price text, e.g. "$7,837,000". */
  priceLabel: string;
  onClick: (e: React.MouseEvent) => void;
  /** Card layouts show the PRICE PROMO chip under the price. Nested rows omit it. */
  showChip?: boolean;
  /**
   * When set, a "Share" link sits under the price and opens the shareable promo
   * designs for this unit. Omitted for agents without the share tools.
   */
  onShareClick?: (e: React.MouseEvent) => void;
}

/**
 * Enlarged hit target for opening the price-history chart from a Price Promo
 * unit card. The whole price + chip block is tappable so fingers don't miss
 * the narrow underlined price and hit the card (project navigation) instead.
 *
 * With `onShareClick` a second, smaller "Share" control sits beneath it — a
 * sibling rather than a nested button, which HTML does not allow.
 */
const PricePromoPriceButton: React.FC<PricePromoPriceButtonProps> = ({
  priceLabel,
  onClick,
  showChip = true,
  onShareClick,
}) => {
  const priceButton = (
  <Box
    component="button"
    type="button"
    aria-label={`Open price history for ${priceLabel}`}
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: 0.5,
      flexShrink: 0,
      m: 0,
      // ≥44px finger target; chip is included in the hit area
      minWidth: showChip ? 96 : 72,
      minHeight: showChip ? 52 : 40,
      py: 0.75,
      px: 1,
      border: '1px solid transparent',
      borderRadius: 1.5,
      bgcolor: 'transparent',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      appearance: 'none',
      font: 'inherit',
      textAlign: 'right',
      transition: 'background-color 0.15s ease, border-color 0.15s ease',
      '&:hover, &:focus-visible': {
        bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.1)',
        borderColor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.35)',
        outline: 'none',
      },
      '&:active': {
        bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.16)',
      },
    }}
  >
    <Typography
      variant="body2"
      component="span"
      sx={{
        color: 'var(--color-success)',
        fontWeight: 700,
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        lineHeight: 1.2,
      }}
    >
      {priceLabel}
    </Typography>
    {showChip && (
      <Chip
        icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
        label="PRICE PROMO"
        size="small"
        // Let the parent button receive the tap (chips can swallow clicks on iOS).
        sx={{
          pointerEvents: 'none',
          bgcolor: 'rgba(var(--color-success-rgb, 76, 175, 80), 0.15)',
          color: 'var(--color-success)',
          fontWeight: 700,
        }}
      />
    )}
  </Box>
  );

  if (!onShareClick) return priceButton;

  return (
    // `stretch` widens Share to the price block's full width: the label stays the
    // small right-aligned link it looks like, but the tap area runs the whole way
    // across instead of hugging the two words.
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.25, flexShrink: 0 }}>
      {priceButton}
      <Box
        component="button"
        type="button"
        aria-label={`Share this price promo (${priceLabel})`}
        onClick={(e) => {
          e.stopPropagation();
          onShareClick(e);
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          m: 0,
          px: 1,
          py: 0.4,
          minHeight: 30,
          border: '1px solid transparent',
          borderRadius: 1.5,
          bgcolor: 'transparent',
          color: 'var(--color-accent)',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          appearance: 'none',
          font: 'inherit',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover, &:focus-visible': {
            bgcolor: 'rgb(var(--color-accent-rgb) / 0.14)',
            borderColor: 'rgb(var(--color-accent-rgb) / 0.4)',
            outline: 'none',
          },
          '&:active': {
            bgcolor: 'rgb(var(--color-accent-rgb) / 0.22)',
          },
        }}
      >
        <IosShareIcon sx={{ fontSize: 15 }} />
        <Typography variant="caption" component="span" sx={{ fontWeight: 700, lineHeight: 1, letterSpacing: 0.2 }}>
          Share
        </Typography>
      </Box>
    </Box>
  );
};

export default PricePromoPriceButton;
